import {TooltipContentText, TooltipContent, TooltipContentSubHeader} from 'Components/JoyRideTooltip';
import GuidesGif from './guides-gif.gif';
import GroupingGif from './grouping-gif.gif';
import HotkeyScreenshot from './HotKeyScreenshot.png';
import {sendSlackAlert} from '../../../slack';
import Button from 'Components/Tailwind/Button';

const BoldColorText: React.FC<React.PropsWithChildren<{color: string}>> = ({color, children}) => (
    <strong className={color}>{children}</strong>
);

export const tutorialCopy = {
    intro: [
        <TooltipContentSubHeader className="text-emerald-400">Why are we here?</TooltipContentSubHeader>,
        <TooltipContentText>
            UI code that doesn't resize correctly is pretty much useless. Rendition's AI does its best, but isn't always
            perfect. We created the Builder so you can easily achieve a layer structure that resizes to larger and
            smaller screens.
        </TooltipContentText>,
        <iframe
            style={{margin: '4px auto'}}
            width="560"
            height="315"
            src="https://www.loom.com/embed/bb579a0f6839496d90473386064a9f09"
            title="video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
        ></iframe>,
        <TooltipContentSubHeader className="text-purple-400">TL:DW?</TooltipContentSubHeader>,
        <TooltipContentText>
            The Builder will allow you to modify the{' '}
            <strong className="text-indigo-500">grouping, positioning, and spacing</strong> of elements to ensure the UI
            we're building <strong className="text-indigo-500">resizes</strong> and{' '}
            <strong className="text-indigo-500">looks just right</strong>
        </TooltipContentText>,
    ],
    grouping: [
        <TooltipContentText>
            <strong className="text-indigo-500">Selecting</strong> and{' '}
            <strong className="text-emerald-400">grouping</strong> elements is one of the core functions of the builder,
            here's a video explaining it:
        </TooltipContentText>,
        <iframe
            style={{margin: '4px auto'}}
            width="560"
            height="315"
            src="https://www.loom.com/embed/45cf501b07b64420adfcb70fce056137"
            title="video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
        ></iframe>,
    ],
    ungrouping: [
        <TooltipContentText>
            Sometimes we want to ungroup elements to get the layer structure we need. This video explains how to{' '}
            <strong className="text-indigo-500">ungroup</strong> elements:
        </TooltipContentText>,
        <iframe
            style={{margin: '4px auto'}}
            width="560"
            height="315"
            src="https://www.loom.com/embed/3456b61758614a6e832522fefbb93e99"
            title="video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
        ></iframe>,
    ],
    // restructuring: [
    //     <TooltipContentText>
    //         You may need to <strong className="text-indigo-500">assign nodes to different groups</strong> or{' '}
    //         <strong className="text-rose-500">absolute position</strong> nodes, let's dig into that!
    //     </TooltipContentText>,
    //     <iframe
    //         style={{margin: '4px auto'}}
    //         width="560"
    //         height="315"
    //         src="https://www.youtube.com/embed/dQw4w9WgXcQ"
    //         title="video player"
    //         frameBorder="0"
    //         allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    //         allowFullScreen
    //     ></iframe>,
    // ],
    groupHotkeys: [
        <TooltipContentText>
            Modifying the grouping structure will also allow you to have more control over the placement and spacing of
            elements.
        </TooltipContentText>,
        <h2 className="text-sm font-semibold text-neutral-200">Hotkeys</h2>,
        <TooltipContentText className="ml-4 gap-2">
            We also have some useful hotkeys for working with and visualizing groups:
        </TooltipContentText>,
        <TooltipContent className="ml-4">
            <div className="flex flex-col items-start gap-2">
                <pre className="inline-flex flex-row items-center gap-0.5 rounded-md border border-neutral-400 px-2 py-1 leading-3">
                    Command+G
                </pre>{' '}
                Group a set of selected layers
            </div>
        </TooltipContent>,
        <TooltipContent className="ml-4">
            <div className="flex flex-col items-start gap-2">
                <pre className="inline-flex flex-row items-center gap-0.5 rounded-md border border-neutral-400 px-2 py-1 leading-3">
                    G
                </pre>{' '}
                Toggle the visibility of grouping lines
            </div>
        </TooltipContent>,
        <img alt="Toggling margin, padding, and width guides" className="mx-auto mt-6 w-1/3" src={GroupingGif} />,
        <h2 className="text-sm font-semibold text-neutral-200">Actions</h2>,
        <TooltipContent className="ml-4 flex flex-col items-start gap-2">
            <pre className="inline-flex flex-col items-center gap-0.5 rounded-md border border-neutral-400 px-2 py-1 leading-3">
                Ungroup Children
            </pre>{' '}
            Moves the children up one layer.
        </TooltipContent>,
        <TooltipContent className="ml-4 flex flex-col items-start gap-2">
            <pre className="inline-flex flex-col items-center gap-0.5 rounded-md border border-neutral-400 px-2 py-1 leading-3">
                Remove Node
            </pre>{' '}
            Removes a node but ungroup and preserves its children in the document.
        </TooltipContent>,
        <TooltipContent className="ml-4 flex flex-col items-start gap-2">
            <pre className="inline-flex flex-col items-center gap-0.5 rounded-md border border-neutral-400 px-2 py-1 leading-3">
                Remove Node With Children
            </pre>{' '}
            Remove a node and all its children (no matter how deep they go)
        </TooltipContent>,
    ],
    sizing: [
        <TooltipContentText>
            In order to help you visualize the <BoldColorText color="text-indigo-500">sizing</BoldColorText> and{' '}
            <BoldColorText color="text-teal-500">positioning</BoldColorText> of elements we have some useful hotkeys
            that you can use to toggle visual guides.
        </TooltipContentText>,
        <TooltipContent className="ml-4 mt-6">
            <div className="flex flex-row gap-4">
                <pre className="inline-flex flex-row items-center gap-0.5 rounded-md border border-neutral-400 px-2 py-1 leading-3">
                    <BoldColorText color="text-green-500">P</BoldColorText>
                </pre>{' '}
                Toggle the visibility of padding guides
            </div>
        </TooltipContent>,
        <TooltipContent className="ml-4">
            <div className="flex flex-row gap-4">
                <pre className="inline-flex flex-row items-center gap-0.5 rounded-md border border-neutral-400 px-2 py-1 leading-3">
                    <BoldColorText color="text-cyan-500">M</BoldColorText>
                </pre>{' '}
                Toggle the visibility of margin guides
            </div>
        </TooltipContent>,
        <TooltipContent className="ml-4">
            <div className="flex flex-row gap-4">
                <pre className="inline-flex flex-row items-center gap-0.5 rounded-md border border-neutral-400 px-2 py-1 leading-3">
                    <BoldColorText color="text-amber-500">W</BoldColorText>
                </pre>{' '}
                Toggle the visibility of width guides
            </div>
        </TooltipContent>,
        <img alt="Toggling margin, padding, and width guides" className="mx-auto mt-6 w-1/2" src={GuidesGif} />,
    ],
    metrics: (userId: string, user: any, finishTutorial: () => void) => [
        <TooltipContentText>Please let us know if this tutorial was helpful to you!</TooltipContentText>,
        <div className="flex flex-row items-center justify-center gap-2 pt-4">
            <Button
                className="h-16 text-4xl"
                outlined
                onClick={() => {
                    sendSlackAlert(userId, user, 'Tutorial Was Helpful', 'tutorial-flow');
                    finishTutorial();
                }}
            >
                🤩
            </Button>
            <Button
                className="h-16 text-4xl"
                outlined
                error
                onClick={() => {
                    sendSlackAlert(userId, user, 'Tutorial Was NOT Helpful', 'tutorial-flow');
                    finishTutorial();
                }}
            >
                😔
            </Button>
        </div>,
    ],
};

export const uiTutorialCopy = {
    shortcuts: [
        <TooltipContentText>
            From this button you can access this popup which has a list of keyboard shortcuts you can you use to
            navigate the product.
        </TooltipContentText>,
        <img alt="Hotkey Menu" className="mx-auto mt-6 w-1/2 rounded-lg" src={HotkeyScreenshot} />,
    ],
    layers: `Here you'll find the different layers that make up the code generated for you by Rendition. You can left click on nodes to select them.
            Shift+Left Click will allow you to select multiple nodes at a time.
            When you have a node selected you can also right click a layer in order to perform viable actions on the layer.`,
    visibility:
        'You can toggle the visibility of layers here in order to hide absolute positioned or flex nodes to more easily target the layer you want to edit.',
    screenSize: 'For compiles that need to be responsive you can simulate screen size changes using the slider',
    codeSandbox:
        'Once you have finished making your changes and are ready to get your code, click this button and we will take you to a code sandbox page with your source code!',
    rightSidebar: [
        <TooltipContentSubHeader className="text-purple-400">What does this sidebar do?</TooltipContentSubHeader>,
        <TooltipContentText>
            With it you will be control <strong className="text-indigo-500">position</strong> (vertical and horizontal
            alignment), <strong className="text-indigo-500">sizing</strong> (width and height), and{' '}
            <strong className="text-indigo-500">spacing</strong> (padding and margin)
        </TooltipContentText>,
        <TooltipContentSubHeader className="text-sky-400">Where are the controls?</TooltipContentSubHeader>,
        <TooltipContentText>Once you have selected an element to modify you will see the controls!</TooltipContentText>,
    ],
    downloadAssets: 'If you have any assets in your project you can download them here.',
    copyForWebflow:
        "If you're using Webflow you can copy the code generated here and paste it into your Webflow project.",
    copyForTailwind: "If you're using Tailwind you can copy the code generated here and paste it into your project.",
};
